import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { statsData } from "@/mocks/home";

function useCountUp(end: number, duration: number = 2000) {
  const [count, setCount] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !started) {
          setStarted(true);
        }
      },
      { threshold: 0.3 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    let startTime: number | null = null;
    const animate = (timestamp: number) => {
      if (!startTime) startTime = timestamp;
      const progress = Math.min((timestamp - startTime) / duration, 1);
      setCount(Math.floor(progress * end));
      if (progress < 1) requestAnimationFrame(animate);
    };
    requestAnimationFrame(animate);
  }, [started, end, duration]);

  return { count, ref };
}

function StatItem({
  value,
  suffix,
  labelKey,
}: {
  value: number;
  suffix: string;
  labelKey: string;
}) {
  const { count, ref } = useCountUp(value, 2500);
  const { t } = useTranslation("home");

  return (
    <div ref={ref} className="text-center px-4">
      <h4 className="text-sm md:text-base text-white/80 font-medium mb-2">
        {t(`stats.${labelKey}`)}
      </h4>
      <div className="text-4xl md:text-5xl font-bold text-white">
        {count}
        {suffix}
      </div>
    </div>
  );
}

export default function StatsSection() {
  const { t } = useTranslation("home");

  return (
    <section className="relative w-full py-16 md:py-24 overflow-hidden">
      <img
        src={statsData.backgroundImage}
        alt="Ventra Core stats"
        className="absolute inset-0 w-full h-full object-cover object-top"
      />
      <div className="absolute inset-0 bg-black/50" />
      <div className="relative z-10 w-full px-4 md:px-6">
        <div className="text-center mb-10 md:mb-14">
          <h3 className="text-2xl md:text-3xl font-bold text-white mb-2 uppercase tracking-wide">
            {t("stats.title")}
          </h3>
          <p className="text-sm text-white/70">{t("stats.subtitle")}</p>
          <div className="w-16 h-1 bg-white/50 mx-auto mt-4" />
        </div>
        <div className="flex flex-col md:flex-row items-center justify-center gap-8 md:gap-0">
          {statsData.stats.map((stat, index) => (
            <div key={stat.key} className="flex items-center">
              <StatItem
                value={stat.value}
                suffix={stat.suffix}
                labelKey={stat.key}
              />
              {index < statsData.stats.length - 1 && (
                <div className="hidden md:block w-px h-16 bg-white/20 mx-8 lg:mx-16" />
              )}
            </div>
          ))}
        </div>
      </div>
    </section>
  );
}